import { useState } from 'react';
import { assessmentSubmissionsApi } from '../services/api/assessmentSubmissions';
import { calculateDimensionScores } from '../utils/assessmentScoring';
import { generateAIRecommendations } from '../utils/aiRecommendations';

export function useAssessmentForm(formSchema) {
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [results, setResults] = useState(null);

  const totalSections = formSchema?.sections?.length || 7;

  // Update a field value
  const updateField = (sectionId, fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [fieldId]: value
      }
    }));
    
    // Clear error for this field
    if (errors[`${sectionId}.${fieldId}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${sectionId}.${fieldId}`];
        return newErrors;
      });
    }
  };

  // Validate a single section
  const validateSection = (sectionIndex) => {
    const section = formSchema.sections[sectionIndex];
    const sectionData = formData[section.id] || {};
    const sectionErrors = {};

    section.fields.forEach(field => {
      if (field.required) {
        const value = sectionData[field.id];
        
        if (value === undefined || value === null || value === '') {
          sectionErrors[`${section.id}.${field.id}`] = `${field.label} is required`;
        } else if (Array.isArray(value) && value.length === 0) {
          sectionErrors[`${section.id}.${field.id}`] = `Please select at least one option`;
        } else if (field.type === 'email' && value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            sectionErrors[`${section.id}.${field.id}`] = 'Please enter a valid email address';
          }
        }
      }
    });

    setErrors(prev => ({
      ...prev,
      ...sectionErrors
    }));

    return Object.keys(sectionErrors).length === 0;
  };

  // Navigate to next section
  const nextSection = () => {
    if (validateSection(currentSection)) {
      if (currentSection < totalSections - 1) {
        setCurrentSection(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  // Navigate to previous section
  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Go to specific section
  const goToSection = (sectionIndex) => {
    if (sectionIndex >= 0 && sectionIndex < totalSections) {
      setCurrentSection(sectionIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Calculate progress percentage
  const getProgress = () => {
    return ((currentSection + 1) / totalSections) * 100;
  };

  // Helper function to convert radio index to option text
  const getRadioOptionText = (sectionId, fieldId, index) => {
    if (index === undefined || index === null || index === '') return '';
    
    const section = formSchema.sections.find(s => s.id === sectionId);
    if (!section) return String(index);
    
    const field = section.fields.find(f => f.id === fieldId);
    if (!field || !field.options) return String(index);
    
    const numIndex = typeof index === 'string' ? parseInt(index) : index;
    if (isNaN(numIndex) || numIndex < 0 || numIndex >= field.options.length) return String(index);
    
    return field.options[numIndex];
  };

  // Helper function to map scale values to Airtable-friendly labels
  const mapScaleToLabel = (value, fieldType) => {
    if (!value) return '';
    
    const numValue = String(value);
    
    const mappings = {
      business_alignment: {
        '1': '1 - Not aligned at all',
        '2': '2 - Minimally aligned',
        '3': '3 - Moderately aligned',
        '4': '4 - Well aligned',
        '5': '5 - Fully integrated'
      },
      data_quality: {
        '1': '1 - Poor or inconsistent data',
        '2': '2 - Basic data management',
        '3': '3 - Good data governance',
        '4': '4 - Enterprise-grade data infrastructure',
        '5': '5 - Enterprise-grade data infrastructure' // Assuming 5 maps to same as 4
      },
      change_readiness: {
        '1': '1 - Resistant to change',
        '2': '2 - Hesitant but open',
        '3': '3 - Neutral / mixed',
        '4': '4 - Eager and supportive',
        '5': '5 - Change champions'
      }
    };
    
    return mappings[fieldType]?.[numValue] || String(value);
  };

  // Prepare data for submission - maps form data to Airtable schema
  const prepareSubmissionData = () => {
    // Calculate scores
    const scores = calculateDimensionScores(formData);

    // Extract data from each section
    const orgContext = formData.organizational_context || {};
    const currentState = formData.current_state || {};
    const strategy = formData.strategy_dimension || {};
    const data = formData.data_dimension || {};
    const technology = formData.technology_dimension || {};
    const people = formData.people_dimension || {};
    const governance = formData.governance_dimension || {};
    const goals = formData.goals_and_priorities || {};

    // Map to Airtable field names (snake_case)
    return {
      // Contact & Company Info
      contact_name: orgContext.contact_name || '',
      contact_email: orgContext.contact_email || '',
      company_name: orgContext.org_name || '',
      company_size: orgContext.org_size || '',
      industry: orgContext.industry || '',
      role: orgContext.contact_role || '',
      
      // Current State
      current_ai_maturity_stage: currentState.stage_self_assessment || '',
      current_ai_description: currentState.ai_use_cases ? 
        (Array.isArray(currentState.ai_use_cases) ? currentState.ai_use_cases.join(', ') : currentState.ai_use_cases) : '',
      ai_usage_scope: currentState.ai_usage_scope || '',
      
      // Strategy Dimension
      executive_support: getRadioOptionText('strategy_dimension', 'executive_sponsorship', strategy.executive_sponsorship),
      success_metrics: getRadioOptionText('strategy_dimension', 'success_metrics', strategy.success_metrics),
      ai_strategy: getRadioOptionText('strategy_dimension', 'ai_strategy_exists', strategy.ai_strategy_exists),
      business_alignment: mapScaleToLabel(strategy.business_alignment, 'business_alignment'),
      
      // People Dimension
      dedicated_roles: people.dedicated_roles || [],
      training_programs: getRadioOptionText('people_dimension', 'training_provided', people.training_provided),
      change_readiness: mapScaleToLabel(people.change_readiness, 'change_readiness'),
      staff_burden_concerns: people.staff_burden_concerns || [],
      ai_literacy: getRadioOptionText('people_dimension', 'ai_literacy', people.ai_literacy),
      change_management: getRadioOptionText('governance_dimension', 'governance_framework', governance.governance_framework),
      
      // Technology Dimension  
      ai_tools_in_use: currentState.ai_tools_in_use || [],
      integration_level: getRadioOptionText('technology_dimension', 'api_maturity', technology.api_maturity),
      automation_scope: '', // Form doesn't collect this data - leaving empty
      cloud_providers: technology.cloud_providers || [],
      erp_systems: technology.erp_systems || [],
      dev_resources: getRadioOptionText('technology_dimension', 'dev_resources', technology.dev_resources),
      infrastructure_constraints: technology.infrastructure_constraints || [],
      
      // Data Dimension
      data_quality: mapScaleToLabel(data.data_quality, 'data_quality'),
      data_accessibility: getRadioOptionText('data_dimension', 'data_integration', data.data_integration),
      data_inventory: getRadioOptionText('data_dimension', 'data_inventory', data.data_inventory),
      data_security: data.data_security || [],
      structured_vs_unstructured: data.structured_vs_unstructured || [],
      data_cleansing_needed: getRadioOptionText('data_dimension', 'data_cleansing_needed', data.data_cleansing_needed),
      classified_data_handling: getRadioOptionText('data_dimension', 'classified_data_handling', data.classified_data_handling),
      security_posture: data.data_security && Array.isArray(data.data_security) && data.data_security.length > 0 ? 
        (data.data_security.length >= 5 ? 'Advanced security with compliance' : 
         data.data_security.length >= 3 ? 'Basic security protocols' : 'Minimal security measures') 
        : 'Minimal security measures',
        
      // Governance Dimension (mapped to Change Management field)
      // governance_framework: governance.governance_framework || '', // Removed - different field
      ethical_guidelines: getRadioOptionText('governance_dimension', 'ethical_guidelines', governance.ethical_guidelines),
      risk_assessment: getRadioOptionText('governance_dimension', 'risk_assessment', governance.risk_assessment),
      accountability: getRadioOptionText('governance_dimension', 'accountability', governance.accountability),
      data_governance: governance.data_governance || [],
      compliance_requirements: governance.compliance_requirements || [],
      compliance_other: governance.compliance_requirements_other || '',
      
      // Goals & Budget
      deployment_preference: getRadioOptionText('technology_dimension', 'cloud_readiness', technology.cloud_readiness),
      budget_allocated: getRadioOptionText('goals_and_priorities', 'budget_range', goals.budget_range),
      timeline: getRadioOptionText('goals_and_priorities', 'implementation_timeline', goals.implementation_timeline),
      target_stage: goals.target_stage || '',
      priority_use_cases: goals.priority_use_cases || '',
      primary_goals: goals.help_needed || [],
      primary_goals_other: goals.help_needed_other || '',
      biggest_challenges: goals.biggest_concerns || [],
      biggest_challenges_other: goals.biggest_concerns_other || '',
      additional_comments: goals.additional_context || '',
      success_definition: goals.success_definition || '',
      
      // Calculated Scores
      scores: {
        strategy: scores.strategy || 0,
        governance: scores.governance || 0,
        people: scores.people || 0,
        technology: scores.technology || 0,
        data: scores.data || 0,
        overall: scores.overall || 0
      },
      
      // Recommended Stage (calculated based on overall scores)
      recommended_stage: scores.overall >= 4 ? 'Stage 4 - AI Transformation - AI integrated across core operations' :
                        scores.overall >= 3 ? 'Stage 3 - AI Orchestration - Multi-step AI processes with oversight' :
                        scores.overall >= 2 ? 'Stage 2 - AI Integration - AI embedded in systems and workflows' :
                        scores.overall >= 1 ? 'Stage 1 - AI Access - Experimenting with AI tools and learning' : 'Stage 0 - No AI - No active AI usage'
    };
  };

  // Submit the form
  const submitForm = async () => {
    // Validate final section
    if (!validateSection(currentSection)) {
      return false;
    }

    setLoading(true);
    setSubmitError(null);

    try {
      const submissionData = prepareSubmissionData();
      
      // Log submission data for debugging
      console.log('=== FORM SUBMISSION STARTED ===');
      console.log('Form Data:', formData);
      console.log('Prepared Submission Data:', submissionData);
      
      const response = await assessmentSubmissionsApi.submit(submissionData);
      
      console.log('=== FORM SUBMISSION SUCCESS ===');
      console.log('Response:', response);
      
      // Calculate scores
      const scores = calculateDimensionScores(formData);
      
      // Generate AI-powered recommendations
      console.log('=== GENERATING AI RECOMMENDATIONS ===');
      const aiRecommendations = await generateAIRecommendations({
        scores,
        formData,
        currentStage: formData.current_state?.stage_self_assessment,
        targetStage: formData.goals_and_priorities?.target_stage,
      });
      
      // Set results with AI recommendations
      setResults({
        scores,
        submissionData,
        recordId: response[0]?.id,
        recommendations: aiRecommendations, // AI-generated or static fallback
      });
      
      return true;
    } catch (error) {
      console.error('=== FORM SUBMISSION FAILED ===');
      console.error('Error:', error);
      console.error('Error message:', error.message);
      console.error('Error details:', error.error);
      
      // Set user-friendly error message
      const errorMsg = error.message || 'Failed to submit assessment. Please try again.';
      setSubmitError(errorMsg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCurrentSection(0);
    setFormData({});
    setErrors({});
    setResults(null);
    setSubmitError(null);
  };

  return {
    // State
    currentSection,
    formData,
    errors,
    loading,
    submitError,
    results,
    totalSections,
    
    // Actions
    updateField,
    nextSection,
    prevSection,
    goToSection,
    submitForm,
    resetForm,
    validateSection,
    
    // Computed
    progress: getProgress(),
    isFirstSection: currentSection === 0,
    isLastSection: currentSection === totalSections - 1,
    canGoNext: currentSection < totalSections - 1,
    canGoPrev: currentSection > 0,
  };
}

